import {
    ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, Output,
    inject, signal
} from '@angular/core';

/** One level-2 (leaf) row of the cascade. `value` is what `valueChange` emits. */
export interface AppCascadeOption {
    value: string;
    label: string;
}

/** One level-1 row: a category whose submenu holds the committable options.
 *  Groups themselves are never committed — put a "whole category" row as the
 *  first option of the submenu when the category itself is a valid pick
 *  (exactly the shape the pcv week filter's optgroup data already has). */
export interface AppCascadeGroup {
    label: string;
    options: AppCascadeOption[];
}

/**
 * Two-level cascading dropdown (the PrimeNG CascadeSelect interaction, built
 * in-house — no dependency): a select-look trigger opens a panel of category
 * rows; hovering/clicking a category slides out its submenu of leaf options;
 * clicking a leaf commits its `value` and closes. A clear row at the top of
 * the panel commits `''`.
 *
 * Replaces flat `<select>`s with `<optgroup>`s whose per-group option lists
 * are long enough to make the flat popup unwieldy (first consumer: the
 * panchang calendar's Week-day filter — 7 weekdays x 11 occurrence rows).
 *
 * Positioning is ABSOLUTE inside the host (`position: relative`), never
 * body-appended/fixed — fixed-position panels are re-based by the AppModal
 * pane's transform (the R5/R9 failure class), and this component's first home
 * is inside that modal. The submenu flips to the left edge when the viewport
 * has no room on the right (rect reads stay viewport-correct under ancestor
 * transforms; only fixed PLACEMENT breaks).
 *
 * Not a CVA on purpose: consumers so far are plain-value filter bars
 * (`[value]` + `(valueChange)`), matching their sibling native selects.
 */
@Component({
    selector: 'app-cascade-select',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app-cascade-select.component.html',
    styleUrls: ['./app-cascade-select.component.css'],
})
export class AppCascadeSelectComponent {

    @Input() groups: AppCascadeGroup[] = [];
    @Input() value: string | null = '';
    @Input() placeholder = 'Select…';
    /** Label of the top clear row (commits ''). */
    @Input() clearLabel = 'All';
    @Input() ariaLabel = '';
    @Output() valueChange = new EventEmitter<string>();

    readonly open = signal(false);
    /** Index of the group whose submenu is out; null = none. */
    readonly activeGroup = signal<number | null>(null);
    /** Submenu flipped to the host's left edge (no viewport room right). */
    readonly submenuLeft = signal(false);

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

    /** Selected option's label ('' when nothing/unknown value — trigger shows
     *  the placeholder). Plain getter: groups/value are plain inputs. */
    get displayLabel(): string {
        const v = this.value ?? '';
        if (v === '') {
            return '';
        }
        for (const g of this.groups) {
            const hit = g.options.find(o => o.value === v);
            if (hit) {
                return hit.label;
            }
        }
        return '';
    }

    toggle(): void {
        if (this.open()) {
            this.close();
            return;
        }
        this.open.set(true);
        this.activeGroup.set(null);
        // flip the submenu left when the panel's right edge leaves no room
        // for a ~170px submenu (viewport rect reads are transform-safe)
        const rect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
        this.submenuLeft.set(rect.right + 180 > window.innerWidth);
    }

    close(): void {
        this.open.set(false);
        this.activeGroup.set(null);
    }

    setActive(index: number): void {
        this.activeGroup.set(index);
    }

    commit(value: string): void {
        this.valueChange.emit(value);
        this.close();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (this.open() && !(this.el.nativeElement as HTMLElement).contains(event.target as Node)) {
            this.close();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.open()) {
            this.close();
        }
    }
}
